import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import MatrixRain from '@/components/MatrixRain';
import MatrixToggle from '@/components/MatrixToggle';
import VersionBadge from '@/components/VersionBadge';
import { Zap, Trophy, Calendar as CalendarIcon, AlertTriangle, Shield, User, MapPin, Bell, Clock, Users, Star, Gamepad2 } from 'lucide-react';

const GREEN = '#00FF41';
const GREEN_GLOW = 'rgba(0,255,65,0.8)';

const CATEGORIES = [
  { emoji: '🎮', label: 'GAMING' },
  { emoji: '☕', label: 'COFFEE' },
  { emoji: '🎳', label: 'BOWLING' },
  { emoji: '🎬', label: 'CINEMA' },
  { emoji: '🎤', label: 'KARAOKE' },
  { emoji: '🏓', label: 'ARCADE/REC' },
];

const CATEGORY_CARDS = [
  { emoji: '🎮', title: 'GAME LABS', active: 24 },
  { emoji: '☕', title: 'COFFEE SPOTS', active: 38 },
  { emoji: '🎳', title: 'BOWLING', active: 32 },
  { emoji: '🎬', title: 'CINEMA', active: 8 },
  { emoji: '🎤', title: 'KARAOKE', active: 15 },
  { emoji: '🏓', title: 'ARCADE/REC', active: 19 },
];

const STATS = [
  { label: 'VISITORS NOW', value: '1,847', sub: 'Across all venues' },
  { label: 'VENUES ONLINE', value: '116', sub: '98% uptime' },
  { label: 'EVENTS TODAY', value: '34', sub: '12 starting soon' },
  { label: 'AVG RATING', value: '4.6 ★', sub: 'All-ages venues' },
  { label: 'GROUPS ACTIVE', value: '289', sub: 'Friend groups checked in' },
];

const TOP_VENUES = [
  { rank: 1, name: 'Riverside Game Lab', cat: 'GAMING', count: 247 },
  { rank: 2, name: 'Lucky Strike', cat: 'BOWLING', count: 133 },
  { rank: 3, name: "Pins n' Packets", cat: 'BOWLING', count: 156 },
  { rank: 4, name: 'Byte Cafe', cat: 'COFFEE', count: 134 },
  { rank: 5, name: 'Level Up Lounge', cat: 'ARCADE', count: 121 },
  { rank: 6, name: 'The Grind House', cat: 'COFFEE', count: 93 },
  { rank: 7, name: 'Neon Lanes', cat: 'BOWLING', count: 87 },
  { rank: 8, name: 'Pixel Paradise', cat: 'GAMING', count: 76 },
];

const HOURLY = [
  { h: '10AM', v: 20 }, { h: '12PM', v: 32 }, { h: '2PM', v: 48 },
  { h: '4PM', v: 70 }, { h: '6PM', v: 95 }, { h: '8PM', v: 88 }, { h: '10PM', v: 55 },
];

const EVENTS = [
  { emoji: '🎮', title: 'Fortnite Tournament', venue: 'Riverside Game Lab', time: '4:00 PM', age: 'Ages 13-17', badge: '32 SIGNED UP' },
  { emoji: '🎳', title: 'Family Bowl Night', venue: 'Lucky Strike', time: '6:00 PM', age: 'All Ages', badge: 'FAMILY' },
  { emoji: '☕', title: 'Open Mic & Latte Art', venue: 'Byte Cafe', time: '7:00 PM', age: 'Ages 30+', badge: '15 RSVP' },
  { emoji: '🎤', title: 'Karaoke Clash', venue: 'Neon Nights Jr', time: '5:00 PM', age: 'Ages 32+', badge: 'FILLING UP' },
  { emoji: '🏆', title: 'Mario Kart League Finals', venue: 'Level Up Lounge', time: '3:00 PM', age: 'All Ages', badge: 'FINALS' },
];

const SAFETY = [
  { Icon: User, label: 'AGE VERIFICATION', status: 'ACTIVE' },
  { Icon: MapPin, label: 'PARENT TRACKING', status: 'ENABLED' },
  { Icon: AlertTriangle, label: 'EMERGENCY ALERTS', status: 'STANDBY' },
  { Icon: Clock, label: 'CURFEW MONITOR', status: '30 MIN SET' },
];

const FEATURES = [
  { emoji: '🎮', label: 'Game Matchmaking' },
  { emoji: '👥', label: 'Group Finder' },
  { emoji: '📊', label: 'Leaderboards' },
  { emoji: '🎟️', label: 'Event Tickets' },
  { emoji: '🛡️', label: 'Safe Social' },
  { emoji: '🗺️', label: 'Venue Maps' },
  { emoji: '⭐', label: 'Rewards Points' },
  { emoji: '👨‍👩‍👧‍👦', label: 'Family Mode' },
];

const FINAL_STATS = [
  { emoji: '🎮', label: 'GAMING SESSIONS', value: '3,421', sub: 'Today across all venues' },
  { emoji: '👨‍👩‍👧‍👦', label: 'FAMILY GROUPS', value: '156', sub: 'Currently checked in' },
  { emoji: '🏆', label: 'TOURNAMENTS', value: '8', sub: 'Running right now' },
  { emoji: '⭐', label: 'REWARDS EARNED', value: '12.4K', sub: 'Points earned today' },
];

const glowText = { color: GREEN, textShadow: `0 0 8px ${GREEN_GLOW}` };
const monoFont = { fontFamily: 'JetBrains Mono, ui-monospace, monospace' };

export default function JuniorDashboard() {
  const [tribeStarted, setTribeStarted] = useState(false);

  return (
    <div className="relative min-h-screen bg-black text-white" style={monoFont}>
      <MatrixRain />
      <MatrixToggle />
      <VersionBadge variant="junior-v1" />

      <div className="relative z-10 max-w-5xl mx-auto p-4 sm:p-6 space-y-5">
        {/* OS Header bar */}
        <div className="flex items-center justify-between border-b border-emerald-500/30 pb-2">
          <span className="text-[10px] sm:text-xs tracking-widest" style={glowText}>NIGHTBUZZ OS v1.0.0</span>
          <span className="text-xs sm:text-sm font-black tracking-widest" style={glowText}>ALL-AGES OPERATOR HUB</span>
          <span className="text-[10px] sm:text-xs tracking-widest flex items-center gap-1" style={glowText}>
            STATUS: <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> LIVE
          </span>
        </div>

        {/* Hero */}
        <div className="text-center py-2">
          <h1
            data-testid="junior-hero-title"
            className="font-black tracking-tight"
            style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 'clamp(2.5rem, 8vw, 4.5rem)', color: GREEN, textShadow: `0 0 20px ${GREEN_GLOW}` }}
          >
            NIGHTBUZZ
          </h1>
          <p
            className="font-black tracking-widest -mt-1"
            style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 'clamp(1.5rem, 5vw, 2.5rem)', color: GREEN, textShadow: `0 0 15px ${GREEN_GLOW}` }}
          >
            JUNIOR
          </p>
          <p className="text-xs sm:text-sm tracking-wider mt-1" style={glowText}>
            THE OPERATING SYSTEM FOR ALL-AGES ENTERTAINMENT
          </p>
          <div className="flex justify-center flex-wrap gap-2 sm:gap-4 mt-3 border border-emerald-500/30 rounded-lg px-3 py-2 max-w-xl mx-auto">
            {CATEGORIES.map((c) => (
              <span key={c.label} className="text-[10px] sm:text-xs flex items-center gap-1" style={glowText}>
                <span className="text-base">{c.emoji}</span> {c.label}
              </span>
            ))}
          </div>
        </div>

        {/* START TRIBE button */}
        <div className="flex justify-center">
          <button
            data-testid="start-tribe-btn"
            onClick={() => setTribeStarted((v) => !v)}
            className="flex items-center gap-2 px-8 py-4 rounded-full font-black tracking-widest text-white text-sm sm:text-base transition hover:scale-105"
            style={{
              background: 'linear-gradient(90deg, #00FF41, #00CC00)',
              boxShadow: '0 0 30px rgba(0,255,65,0.7)',
              fontFamily: 'Orbitron, sans-serif',
            }}
          >
            <Zap className="h-5 w-5 text-yellow-300" />
            {tribeStarted ? 'TRIBE ACTIVE' : 'START TRIBE'}
          </button>
        </div>

        {/* Category cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {CATEGORY_CARDS.map((c) => (
            <Card
              key={c.title}
              data-testid={`junior-cat-${c.title.toLowerCase().replace(/[^a-z]/g, '-')}`}
              className="bg-black/60 border border-emerald-500/30 p-4 text-center hover:border-emerald-400/70 transition"
            >
              <div className="text-3xl mb-1">{c.emoji}</div>
              <p className="text-xs font-bold" style={glowText}>{c.title}</p>
              <p className="text-[10px] mt-1 text-emerald-300/80">{c.active} ACTIVE</p>
            </Card>
          ))}
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {STATS.map((s) => (
            <Card key={s.label} className="bg-black/60 border border-emerald-500/30 p-3">
              <p className="text-[10px] font-mono tracking-widest" style={glowText}>{s.label}</p>
              <p className="text-2xl sm:text-3xl font-black text-white leading-tight">{s.value}</p>
              <p className="text-[10px] text-zinc-400">{s.sub}</p>
            </Card>
          ))}
        </div>

        {/* Top venues */}
        <Card className="bg-black/60 border border-emerald-500/30 p-4">
          <p className="text-xs font-mono tracking-widest flex items-center gap-2 mb-2" style={glowText}>
            <Trophy className="h-4 w-4" /> TOP VENUES RIGHT NOW
          </p>
          <ol className="space-y-1">
            {TOP_VENUES.map((v) => (
              <li key={v.rank} className="flex justify-between text-xs text-emerald-100/90 font-mono">
                <span>#{v.rank} {v.name}</span>
                <span className="text-emerald-300">{v.cat}: {v.count}</span>
              </li>
            ))}
          </ol>
        </Card>

        {/* Hourly activity bar chart */}
        <Card className="bg-black/60 border border-emerald-500/30 p-4">
          <p className="text-xs font-mono tracking-widest flex items-center gap-2 mb-3" style={glowText}>
            <CalendarIcon className="h-4 w-4" /> HOURLY ACTIVITY (ALL VENUES)
          </p>
          <div className="flex items-end justify-between h-24 gap-1">
            {HOURLY.map((b) => (
              <div key={b.h} className="flex-1 flex flex-col items-center gap-1">
                <div
                  className="w-full rounded-t"
                  style={{ height: `${b.v}%`, background: `linear-gradient(180deg, #00FF41, #007A20)`, boxShadow: '0 0 8px rgba(0,255,65,0.5)' }}
                />
                <span className="text-[9px] font-mono" style={glowText}>{b.h}</span>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-emerald-200/70 mt-2 text-center">Peak: 6PM-8PM (After-school & family hours)</p>
        </Card>

        {/* Upcoming events */}
        <Card className="bg-black/60 border border-emerald-500/30 p-4">
          <p className="text-xs font-mono tracking-widest flex items-center gap-2 mb-3" style={glowText}>
            <Bell className="h-4 w-4 text-red-400" /> UPCOMING EVENTS
          </p>
          <div className="space-y-2">
            {EVENTS.map((e) => (
              <div key={e.title} className="flex items-center justify-between gap-2 border border-emerald-500/20 rounded p-2 bg-black/40">
                <div className="flex items-start gap-2 min-w-0">
                  <span className="text-lg">{e.emoji}</span>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-white truncate">{e.title}</p>
                    <p className="text-[10px] text-emerald-200/70 truncate">{e.venue} • {e.time} • {e.age}</p>
                  </div>
                </div>
                <Badge className="bg-emerald-600 text-black text-[10px] whitespace-nowrap">{e.badge}</Badge>
              </div>
            ))}
          </div>
        </Card>

        {/* Safety & Compliance */}
        <Card className="bg-black/60 border border-emerald-500/30 p-4">
          <p className="text-xs font-mono tracking-widest flex items-center gap-2 mb-3" style={glowText}>
            <Shield className="h-4 w-4" /> SAFETY & COMPLIANCE
          </p>
          <div className="grid grid-cols-2 gap-2">
            {SAFETY.map(({ Icon, label, status }) => (
              <div key={label} className="border border-emerald-500/20 rounded p-2 bg-black/40 flex items-center gap-2">
                <Icon className="h-4 w-4 text-emerald-300" />
                <div>
                  <p className="text-[10px] font-mono" style={glowText}>{label}</p>
                  <p className="text-[10px] text-white">{status}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Junior Features grid */}
        <Card className="bg-black/60 border border-emerald-500/30 p-4">
          <p className="text-xs font-mono tracking-widest flex items-center gap-2 mb-3" style={glowText}>
            <Zap className="h-4 w-4 text-yellow-300" /> JUNIOR FEATURES
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {FEATURES.map((f) => (
              <button
                key={f.label}
                data-testid={`junior-feature-${f.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                className="flex items-center gap-2 px-3 py-2 rounded border border-emerald-500/30 bg-black/40 text-xs text-emerald-100 hover:bg-emerald-900/20 hover:border-emerald-400 transition"
              >
                <span className="text-base">{f.emoji}</span> {f.label}
              </button>
            ))}
          </div>
        </Card>

        {/* Final stats */}
        <div className="grid grid-cols-2 gap-3">
          {FINAL_STATS.map((s) => (
            <Card key={s.label} className="bg-black/60 border border-emerald-500/30 p-3 text-center">
              <div className="text-2xl mb-1">{s.emoji}</div>
              <p className="text-[10px] font-mono" style={glowText}>{s.label}</p>
              <p className="text-2xl font-black text-white">{s.value}</p>
              <p className="text-[9px] text-zinc-400">{s.sub}</p>
            </Card>
          ))}
        </div>

        {/* Footer */}
        <div className="text-center pt-4 border-t border-emerald-500/20">
          <p className="text-xs sm:text-sm tracking-widest italic" style={glowText}>
            &ldquo;WHAT IF I TOLD YOU, FUN HAS NO AGE LIMIT.&rdquo;
          </p>
          <p className="text-[10px] font-mono mt-1 text-emerald-300/60">— NIGHTBUZZ JUNIOR</p>
          <p className="text-[9px] font-mono mt-2 text-emerald-200/40">POWERED BY NIGHTBUZZ OS V1.0.0 · ALL-AGES SAFE · UPTIME: 98%</p>
        </div>
      </div>
    </div>
  );
}
